import React, { useState, useEffect } from "react";
import { GamePhase, PlayerRole, Player } from "../../context/GameContext";
import { useGame } from "../../context/GameContext";

interface ActionPanelProps {
  phase: GamePhase;
  role: PlayerRole;
  players: Player[];
  isAlive: boolean;
}

const ActionPanel: React.FC<ActionPanelProps> = ({
  phase,
  role,
  players,
  isAlive,
}) => {
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null);
  const [actionTaken, setActionTaken] = useState(false);
  const { sendMafiaAction, sendDoctorAction, sendDetectiveAction, gameState } =
    useGame();

  // Reset action state when phase changes
  useEffect(() => {
    setSelectedPlayer(null);
    setActionTaken(false);
  }, [phase]);

  // Helper functions
  const handleSelectPlayer = (playerId: string) => {
    if (!isAlive || actionTaken || phase !== GamePhase.NIGHT) return;

    if (role === PlayerRole.VILLAGER) return;

    setSelectedPlayer(playerId);
  };

  const handleAction = () => {
    if (!selectedPlayer || !isAlive || actionTaken) return;

    switch (role) {
      case PlayerRole.MAFIA:
        sendMafiaAction(selectedPlayer);
        break;
      case PlayerRole.DOCTOR:
        sendDoctorAction(selectedPlayer);
        break;
      case PlayerRole.DETECTIVE:
        sendDetectiveAction(selectedPlayer);
        break;
      default:
        return;
    }

    setActionTaken(true);
  };

  const getActionName = () => {
    switch (role) {
      case PlayerRole.MAFIA:
        return "Kill";
      case PlayerRole.DOCTOR:
        return "Save";
      case PlayerRole.DETECTIVE:
        return "Investigate";
      default:
        return "Action";
    }
  };

  const getActionDescription = () => {
    if (!isAlive) return "You're dead and cannot take actions.";

    if (phase !== GamePhase.NIGHT) {
      return "Actions can only be taken during the night phase.";
    }

    switch (role) {
      case PlayerRole.MAFIA:
        return "Select a player to eliminate.";
      case PlayerRole.DOCTOR:
        return "Select a player to protect from the mafia.";
      case PlayerRole.DETECTIVE:
        return "Select a player to investigate whether they're a mafia member.";
      case PlayerRole.VILLAGER:
        return "You're a villager and have no special night actions.";
      default:
        return "";
    }
  };

  // Show day phase content when it's day
  if (phase === GamePhase.DAY) {
    return (
      <div className="card-transparent h-full flex flex-col">
        <h2 className="text-xl font-semibold mb-4">Day Phase</h2>

        <div className="flex-grow flex flex-col items-center justify-center text-center py-8">
          <div className="text-5xl mb-6">☀️</div>
          <h3 className="text-lg font-medium mb-2">Time to discuss!</h3>
          <p className="text-slate-400 max-w-md">
            Discuss with other players and try to identify who might be the
            mafia. Use the chat panel to communicate.
            {!isAlive &&
              " Since you're dead, you can only observe the discussion."}
          </p>

          {gameState.killResult && (
            <div className="mt-8 p-4 bg-red-900/30 border border-red-800 rounded-md max-w-md w-full">
              <h4 className="font-semibold text-red-400 mb-2">
                Last Night's Events
              </h4>
              <p>
                {players.find((p) => p.id === gameState.killResult)?.name ||
                  "Someone"}{" "}
                was killed by the mafia!
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // If it's night and you're a villager or dead
  if (role === PlayerRole.VILLAGER || !isAlive) {
    return (
      <div className="card-transparent h-full flex flex-col">
        <h2 className="text-xl font-semibold mb-4">Night Phase</h2>

        <div className="flex-grow flex flex-col items-center justify-center text-center py-8">
          <div className="text-5xl mb-6">🌙</div>
          <h3 className="text-lg font-medium mb-2">
            {!isAlive
              ? "You are dead and watching the game"
              : "You are sleeping"}
          </h3>
          <p className="text-slate-400 max-w-md">
            {!isAlive
              ? "As a ghost, you can observe the game but cannot participate."
              : "As a villager, you don't have any special night actions. The mafia, doctor, and detective are making their moves..."}
          </p>
        </div>
      </div>
    );
  }

  // Night action interface for special roles
  return (
    <div className="card-transparent h-full flex flex-col">
      <h2 className="text-xl font-semibold mb-4">
        {role.charAt(0).toUpperCase() + role.slice(1)} Action
      </h2>

      <p className="text-sm text-slate-300 mb-6">{getActionDescription()}</p>

      {!actionTaken ? (
        <>
          <div className="flex-grow">
            <h3 className="text-md font-medium mb-3">Select a player:</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {players.map((player) => (
                <div
                  key={player.id}
                  onClick={() => handleSelectPlayer(player.id)}
                  className={`
                    p-3 rounded-md cursor-pointer transition-colors
                    ${
                      selectedPlayer === player.id
                        ? "bg-indigo-700 border border-indigo-500"
                        : "bg-slate-700 hover:bg-slate-600"
                    }
                  `}
                >
                  {player.name}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={handleAction}
              disabled={!selectedPlayer}
              className={`btn w-full ${
                selectedPlayer ? "btn-primary" : "btn-secondary opacity-50"
              }`}
            >
              {getActionName()} Selected Player
            </button>
          </div>
        </>
      ) : (
        <div className="flex-grow flex flex-col items-center justify-center text-center py-8">
          <div className="text-5xl mb-6">✅</div>
          <h3 className="text-lg font-medium mb-2">Action Submitted</h3>
          <p className="text-slate-400 max-w-md">
            You've taken your night action. Waiting for other players to finish
            their actions...
          </p>
        </div>
      )}
    </div>
  );
};

export default ActionPanel;
